import { Icon } from "../Icon";
import PaperSlot from "./PaperSlot";
import { RenderTags } from "./RenderTags";
import { RenderInput } from "./RenderInput";
import { RenderOption } from "./RenderOption";
import { ReactNode, forwardRef, useState } from "react";
import { Autocomplete, AutocompleteProps, Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";

/**
 * 자동완성 드롭다운 선택 컴포넌트의 props.
 * MUI Autocomplete 래핑 — `freeSolo` 사용 시 옵션에 없는 문자열 입력 허용.
 *
 * @example
 * ```tsx
 * // 단일 선택
 * <ListSelect options={["옵션1", "옵션2"]} value={selected} onChange={(_, v) => setSelected(v)} ariaLabel="선택" />
 *
 * // 다중 선택 + 체크박스 + 전체 선택
 * <ListSelect options={categories} multiple checkbox hasAllSelect value={selected} onChange={...} />
 * ```
 */
export interface ListSelectProps
  extends Omit<
    AutocompleteProps<any, boolean, boolean, boolean>,
    "renderInput" | "options"
  > {
  /** 선택 가능한 옵션 문자열 목록 */
  options: string[];
  /** 다중 선택 모드 활성화 */
  multiple?: boolean;
  placeholder?: string;
  /** 옵션 항목에 체크박스 표시 — `multiple` 과 함께 자주 쓴다 */
  checkbox?: boolean;
  /** 다중 선택 시 표시할 최대 태그 수 (초과분은 +N 으로 축약) */
  limitTags?: number;
  /** 드롭다운 리스트의 최대 높이(px) */
  maxHeight?: number;
  /** "전체 선택" 항목 노출 */
  hasAllSelect?: boolean;
  /** "전체 선택" 의 현재 활성 여부 */
  allSelected?: boolean;
  /** "전체 선택" 토글 콜백 */
  onAllSelect?: (currentAllSelected: boolean) => void;
  showLabel?: boolean;
  showDivider?: boolean;
  /** 입력 필드에 주입할 `aria-label` — 화면에 보이는 라벨을 렌더링하지 않는다 */
  ariaLabel?: string;
  /** 드롭다운 상단에 끼울 ReactNode */
  header?: ReactNode;
  /** 드롭다운 하단에 끼울 ReactNode */
  footer?: ReactNode;
  disableClearable?: boolean;
  gap?: number;
  /** 옵션에 없는 자유 문자열 입력 허용 */
  freeSolo?: boolean;
  helperText?: string;
  /** 키보드 입력 비활성화 (선택만 허용) */
  disabledTypeInput?: boolean;
  /** 드롭다운 펼침 방향 — 기본 `"bottom"` */
  placement?: "top" | "bottom";
}

export const ListSelect = forwardRef<HTMLDivElement, ListSelectProps>(
  (
    {
      options,
      multiple = false,
      placeholder = "선택",
      checkbox = false,
      limitTags = 2,
      maxHeight,
      hasAllSelect = false,
      allSelected = false,
      onAllSelect,
      showLabel = false,
      showDivider = false,
      ariaLabel,
      header,
      footer,
      disableClearable = true,
      gap = 0,
      freeSolo = false,
      helperText,
      disabledTypeInput = false,
      placement = "bottom",
      sx,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);

    const slotProps = {
      listbox: {
        sx: {
          ...listBox,
          maxHeight: maxHeight ? `${maxHeight}px` : "none",
          marginTop: gap > 0 ? `${gap}px` : 0,
        },
      },
      paper: {
        component: PaperSlot,
        hasAllSelect,
        header,
        footer,
        allSelected,
        onAllSelect,
      },
      popper: {
        placement: placement,
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, gap > 0 ? gap : 8],
            },
          },
        ],
      },
    };

    // 열림/닫힘은 Autocomplete 의 onOpen/onClose 가 단독 소유한다 —
    // 래퍼에 별도 클릭 토글을 두면 input 클릭이 onOpen 과 이중 토글로
    // 상쇄되어 드롭다운이 열리지 않는다(버블링).
    return (
      <Box sx={{ overflow: "visible" }}>
        <Autocomplete
          ref={ref}
          open={open}
          onOpen={() => setOpen(true)}
          value={props.value}
          options={options}
          multiple={multiple}
          freeSolo={freeSolo}
          limitTags={limitTags}
          slotProps={slotProps}
          disableListWrap={true}
          disableCloseOnSelect={multiple}
          disableClearable={disableClearable}
          onClose={(event, reason) => {
            // 옵션 선택이나 입력 필드 클릭 시에는 닫지 않음
            if (reason === "selectOption" || reason === "toggleInput") {
              return;
            }
            setOpen(false);
          }}
          popupIcon={<Icon name="16/arrow/bottom" size={16} clickable />}
          sx={{ ...defaultStyles, ...sx }}
          renderInput={(params) => {
            const shouldShowPlaceholder =
              !multiple ||
              !props.value ||
              (Array.isArray(props.value) && props.value.length === 0);

            return (
              <RenderInput
                params={params}
                showLabel={showLabel}
                showDivider={showDivider}
                helperText={helperText}
                disabledTypeInput={disabledTypeInput}
                placeholder={shouldShowPlaceholder ? placeholder : ""}
                ariaLabel={ariaLabel}
              />
            );
          }}
          renderOption={
            checkbox
              ? (props, option, state) => (
                  <RenderOption props={props} option={option} state={state} />
                )
              : undefined
          }
          renderTags={(selected) => (
            <RenderTags
              selected={selected}
              limitTags={limitTags}
              onChange={props.onChange}
            />
          )}
          {...props}
        />
      </Box>
    );
  }
);

ListSelect.displayName = "ListSelect";

const defaultStyles = {
  width: "100%",
  cursor: "pointer",
  overflow: "visible",
  "& .MuiInputBase-root": {
    overflow: "visible",
    backgroundColor: "#ffffff",
    overflowX: "auto",
    overflowY: "hidden",
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    padding: 0,
    paddingLeft: "8px",
    paddingRight: "8px",
    "&::-webkit-scrollbar-button": {
      display: "none",
    },
    "& input::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type='number']": {
      MozAppearance: "textfield",
    },
  },
} as const;

const listBox = {
  zIndex: 1001,
  padding: "8px",
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "12px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f5f5f5",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#d0d0d0",
    borderRadius: "10px",
    border: "3px solid #f5f5f5",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#b0b0b0",
  },
  "& .MuiAutocomplete-option": {
    "&[aria-selected='true']": {
      borderRadius: "4px",
      fontWeight: 600,
      color: COLOR_CONSTANTS.primary.main,
      backgroundColor: alpha(COLOR_CONSTANTS.primary.main, 0.1),
    },
  },
} as const;
